import { ALTERSKLASSEN, TASK_STATUS_LABELS, type Altersklasse } from '@netcon-work/shared'
import { useHinweis } from './hinweis'

/**
 * Liegezeiten-Matrix: wie viele Aufgaben liegen wie lange wo (Konzept Kap. 4.6).
 *
 * Optik aus dem Mockup (`.age-tbl`): Zeilen sind Stati, Spalten Alters-Klassen,
 * jede Zelle eine getönte Kachel mit der Anzahl.
 *
 * ## Was hier Farbe bedeutet – und was nicht
 *
 * Die Tönung kommt aus der **Spalte**, nicht aus dem Wert. „> 7 Tage" ist immer
 * das ernste Feld, egal ob eine oder zwanzig Aufgaben dort liegen; die Zahl in
 * der Kachel trägt die Menge. Die Farbe doppelt also nicht, was die Zahl schon
 * sagt – sie sagt, wie schlimm die Spalte ist.
 *
 * Die Spaltenköpfe („< 3 Tage", „3–7 Tage", „> 7 Tage") sind dabei die
 * Beschriftung zur Ampel: die Bedeutung hängt nie an der Farbe allein.
 *
 * **Nullen bleiben grau.** Das Mockup färbt auch leere Zellen rot – gut gemeint
 * als „Spalte ist gefährlich", aber im Daily liest sich eine rote Null als
 * Problem, wo tatsächlich die beste Nachricht steht: dort hängt nichts. Eine
 * Kachel trägt ihre Ampelfarbe nur, wenn auch etwas drinsteht.
 */

const KLASSE_KOPF: Record<Altersklasse, string> = {
  unter3: '< 3 Tage',
  drei_bis_sieben: '3–7 Tage',
  bis90: '8–90 Tage',
  ueber90: '> 90 Tage',
}

/** Ampel je Spalte. Werte aus dem Mockup (`.age-ok` / `.age-mid` / `.age-bad`). */
const KLASSE_STIL: Record<Altersklasse, string> = {
  unter3: 'bg-[#eef7ee] text-[#1d6b34]',
  drei_bis_sieben: 'bg-[#fdf3dc] text-[#8a6100]',
  bis90: 'bg-[#fdeaea] text-[#b02a2a]',
  /**
   * **Grau, nicht rot** (E-358): über 90 Tage ist kein Stau mehr, sondern Altlast – sie steht
   * im Reiter „Aufräumen". Rot hieße „heute handeln", und das stimmt für sie nicht.
   */
  ueber90: 'bg-[#efeee9] text-ink2',
}

/** Was die Ampel meint – als Text, nicht nur als Ton. */
const KLASSE_BEDEUTUNG: Record<Altersklasse, string> = {
  unter3: 'frisch',
  drei_bis_sieben: 'liegt schon',
  bis90: 'hängt',
  ueber90: 'Altlast – siehe Aufräumen',
}

export type LiegezeitZeile = {
  status: string
  unter3: number
  drei_bis_sieben: number
  bis90: number
  ueber90: number
  summe: number
}

export function LiegezeitenMatrix({ zeilen }: { zeilen: LiegezeitZeile[] }) {
  const { anMark, feld } = useHinweis()

  const haengt = zeilen
    .filter((z) => z.bis90 > 0)
    .sort((a, b) => b.bis90 - a.bis90)
    .slice(0, 2)

  return (
    <>
      <div className="flex items-baseline gap-2">
        <b className="text-[13px]">Liegezeiten</b>
        <span className="text-xs text-muted">
          wie viele Aufgaben liegen wie lange wo · ohne Zurückgestellt
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-[560px] mt-2 w-full border-collapse text-xs">
          <thead>
            <tr>
              <th className="border-b border-line px-1.5 py-[5px] text-left text-[10.5px] font-semibold text-ink2">
                Ort
              </th>
              {ALTERSKLASSEN.map((k) => (
                <th
                  key={k}
                  className="border-b border-line px-1.5 py-[5px] text-right text-[10.5px] font-semibold text-ink2"
                >
                  {KLASSE_KOPF[k]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zeilen.map((zeile) => (
              <tr key={zeile.status}>
                <td className="border-b border-line px-1.5 py-1.5 font-semibold">
                  {TASK_STATUS_LABELS[zeile.status as keyof typeof TASK_STATUS_LABELS] ??
                    zeile.status}
                </td>
                {ALTERSKLASSEN.map((k) => {
                  const anzahl = zeile[k]
                  return (
                    <td key={k} className="border-b border-line px-1.5 py-1.5 text-right">
                      <span
                        tabIndex={0}
                        {...anMark(
                          <>
                            <b>
                              {TASK_STATUS_LABELS[
                                zeile.status as keyof typeof TASK_STATUS_LABELS
                              ] ?? zeile.status}
                            </b>{' '}
                            · {KLASSE_KOPF[k]}
                            <br />
                            {anzahl === 0
                              ? 'nichts – hier liegt gerade nichts'
                              : `${anzahl} ${anzahl === 1 ? 'Aufgabe' : 'Aufgaben'} (${KLASSE_BEDEUTUNG[k]})`}
                          </>,
                        )}
                        className={`tabular inline-block min-w-[30px] rounded-[7px] px-[7px] py-[3px] font-semibold ${
                          anzahl === 0 ? 'text-muted' : KLASSE_STIL[k]
                        }`}
                      >
                        {anzahl}
                      </span>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {haengt.length > 0 && (
        <p className="mt-2.5 text-[11px] text-muted">
          ⚠{' '}
          {haengt
            .map(
              (z) =>
                `${z.bis90} ${z.bis90 === 1 ? 'Aufgabe' : 'Aufgaben'} seit 8–90 Tagen in „${
                  TASK_STATUS_LABELS[z.status as keyof typeof TASK_STATUS_LABELS] ?? z.status
                }"`,
            )
            .join(' · ')}
          . Zurückgestellt zählt nicht mit; was länger als 90 Tage liegt, steht unter „Aufräumen".
        </p>
      )}
      {feld}
    </>
  )
}
